"use client"

import { useState, useEffect } from "react"
import { gerarHorarios } from "../agendamento/gerarHorarios"
import { supabase } from "../supabaseClient"
import HorarioList from "../components/Horarios/HorarioList"
import HorarioEditor from "../components/Horarios/HorarioEditor"
import "../styles/HorariosPage.css"
import { useAuth } from "../context/AuthContext"
import { applyInstitutionFilter } from "../lib/tenant"
import { BookOpen } from "lucide-react"

export default function HorariosPage() {
  const { isAdmin, instituicaoId } = useAuth()
  const [updateList, setUpdateList] = useState(false)
  const [loading, setLoading] = useState(false)
  const [mensagem, setMensagem] = useState("")

  const [professores, setProfessores] = useState([])
  const [turmas, setTurmas] = useState([])
  const [cursos, setCursos] = useState([])

  const [professorId, setProfessorId] = useState("")
  const [turmaId, setTurmaId] = useState("")
  const [cursoId, setCursoId] = useState("")

  const [editorAberto, setEditorAberto] = useState(false)
  const [turmaParaEditar, setTurmaParaEditar] = useState(null)

  const [modoDistribuicao, setModoDistribuicao] = useState("automatico")
  const [config, setConfig] = useState(null)
  const [showHelpModal, setShowHelpModal] = useState(false)

  useEffect(() => {
    const fetchConfig = async () => {
      let query = supabase.from("configuracoes_instituicao").select("*")
      if (!isAdmin && instituicaoId) {
        query = query.eq("instituicao_id", instituicaoId)
      }
      const { data } = await query.limit(1).maybeSingle()
      setConfig(data)
    }
    fetchConfig()
  }, [isAdmin, instituicaoId])

  useEffect(() => {
    const fetchData = async () => {
      try {
        let profsQuery = supabase.from("professores").select("id, nome").order("nome")
        profsQuery = applyInstitutionFilter(profsQuery, isAdmin, instituicaoId)
        const { data: profs, error: errProf } = await profsQuery
        if (errProf) throw errProf
        setProfessores(profs || [])

        let turmasQuery = supabase.from("turmas").select("id, nome, curso_id").order("nome")
        turmasQuery = applyInstitutionFilter(turmasQuery, isAdmin, instituicaoId)
        const { data: turmasData, error: errTurma } = await turmasQuery
        if (errTurma) throw errTurma
        setTurmas(turmasData || [])
        let cursosQuery = supabase.from("cursos").select("id, nome").order("nome")
        cursosQuery = applyInstitutionFilter(cursosQuery, isAdmin, instituicaoId)
        const { data: cursosData, error: errCurso } = await cursosQuery
        if (errCurso) throw errCurso
        setCursos(cursosData || [])
      } catch (err) {
        console.error("Erro ao carregar dados:", err.message)
      }
    }
    fetchData()
  }, [isAdmin, instituicaoId])

  const turmasFiltradas = cursoId ? turmas.filter((t) => t.curso_id === cursoId) : turmas

  const handleGerarAutomatico = async () => {
    setLoading(true)
    setMensagem("")
    try {
      const maxTemposPorDia = modoDistribuicao === "automatico" ? (config?.max_tempos_por_dia_disciplina || 2) : modoDistribuicao === "distribuir-3" ? 3 : 2
      const result = await gerarHorarios(maxTemposPorDia, instituicaoId)
      if (result && result.length > 0) {
        setMensagem("Horários gerados e salvos com sucesso!")
        setUpdateList((prev) => !prev)
      } else {
        setMensagem("Não foi possível gerar todos os horários.")
      }
    } catch (err) {
      setMensagem("Erro ao gerar horários: " + err.message)
    } finally {
      setLoading(false)
    }
  }

  const handleAtualizarHorarios = async () => {
    setLoading(true)
    setMensagem("")
    try {
      let deleteQuery = supabase.from("horarios").delete()
      if (!isAdmin && instituicaoId) {
        deleteQuery = deleteQuery.eq("instituicao_id", instituicaoId)
      } else {
        deleteQuery = deleteQuery.neq("id", "00000000-0000-0000-0000-000000000000")
      }
      await deleteQuery

      const maxTemposPorDia = modoDistribuicao === "automatico" ? (config?.max_tempos_por_dia_disciplina || 2) : modoDistribuicao === "distribuir-3" ? 3 : 2
      const result = await gerarHorarios(maxTemposPorDia, instituicaoId)
      if (result && result.length > 0) {
        setMensagem("Horários atualizados com nova ordem aleatória!")
        setUpdateList((prev) => !prev)
      } else {
        setMensagem("Erro ao atualizar horários.")
      }
    } catch (err) {
      setMensagem("Erro ao atualizar: " + err.message)
    } finally {
      setLoading(false)
    }
  }

  const abrirEditor = (id) => {
    setTurmaParaEditar(id)
    // quando abrimos editor, também ajustamos o filtro de turma para que a lista
    // mostre o mesmo conjunto de horários que estamos editando
    setTurmaId(id)
    setEditorAberto(true)
  }

  const fecharEditor = () => {
    setEditorAberto(false)
    setTurmaParaEditar(null)
  }

  const onEditorSave = () => {
    // após salvar no editor, forçamos recarga da listagem e mantemos a turma
    // que estava sendo editada como filtro para que o usuário veja imediatamente
    setUpdateList((prev) => !prev)
    if (turmaParaEditar) {
      setTurmaId(turmaParaEditar)
    }
    setMensagem("Horários atualizados com sucesso!")
  }

  const handleCursoChange = (value) => {
    setCursoId(value)
    setTurmaId("")
  }

  return (
    <div className="horarios-page">
      <h1 className="page-title">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="32"
          height="32"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
          <line x1="16" y1="2" x2="16" y2="6"></line>
          <line x1="8" y1="2" x2="8" y2="6"></line>
          <line x1="3" y1="10" x2="21" y2="10"></line>
        </svg>
        Gerenciamento de Horários
      </h1>



      <div className="top-help-row">
        <div className="help-card-section help-card-section-small">
          <div className="help-card glass" onClick={() => setShowHelpModal(true)}>
            <div className="help-card-content small">
              <div className="help-icon">
                <BookOpen size={24} />
              </div>
              <div className="help-text">
                <h3>Como Criar Horários</h3>
                <p>Veja o passo a passo para montar seus horários.</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="actions-container">
        <div className="distribuicao-option">
          <label className="select-label-option">
            <span className="label-text">Modo de Distribuição:</span>
            <select
              value={modoDistribuicao}
              onChange={(e) => setModoDistribuicao(e.target.value)}
              className="select-distribuicao"
            >
              <option value="automatico">Gerenciamento Automático</option>
              <option value="distribuir-2">Distribuir Aulas (max 2 tempos/dia)</option>
              <option value="distribuir-3">Distribuir Aulas (max 3 tempos/dia)</option>
            </select>
            <small className="hint-text">
              {modoDistribuicao === "automatico"
                ? "Disciplinas nucleares podem ter 4 tempos seguidos"
                : modoDistribuicao === "distribuir-3"
                ? "No máximo 3 tempos da mesma disciplina no mesmo dia"
                : "No máximo 2 tempos da mesma disciplina no mesmo dia"}
            </small>
          </label>
        </div>

        <button className="btn-gerar" onClick={handleGerarAutomatico} disabled={loading}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"></polygon>
          </svg>
          {loading ? "Gerando..." : "Gerar Horários Automáticos"}
        </button>

        <button className="btn-atualizar" onClick={handleAtualizarHorarios} disabled={loading}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2" />
          </svg>
          Atualizar com Novos Dados
        </button>

        <button
          className="btn-editar-horario"
          onClick={() => {
            if (turmaId) {
              abrirEditor(turmaId)
            } else if (turmas.length > 0) {
              const primeiraT = turmas[0]
              setTurmaId(primeiraT.id)
              abrirEditor(primeiraT.id)
            }
          }}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
          </svg>
          Abrir Editor de Horários
        </button>

        {mensagem && (
          <p className={`mensagem ${mensagem.includes("Erro") ? "error" : ""}`}>
            {mensagem.includes("Erro") ? "⚠" : "✓"} {mensagem}
          </p>
        )}
      </div>

      <div className="filtros-container">
        <div className="filtro-group">
          <label>
            <span className="filtro-label">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M22 10v6M2 10l10-5 10 5-10 5z"></path>
                <path d="M6 12v5c3 3 9 3 12 0v-5"></path>
              </svg>
              Curso
            </span>
            <select value={cursoId} onChange={(e) => handleCursoChange(e.target.value)}>
              <option value="">Todos os Cursos</option>
              {cursos.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.nome}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div className="filtro-group">
          <label>
            <span className="filtro-label">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                <circle cx="9" cy="7" r="4"></circle>
                <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
              </svg>
              Turma
            </span>
            <select value={turmaId} onChange={(e) => setTurmaId(e.target.value)}>
              <option value="">Todas as Turmas</option>
              {turmasFiltradas.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.nome}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div className="filtro-group">
          <label>
            <span className="filtro-label">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                <circle cx="12" cy="7" r="4"></circle>
              </svg>
              Professor
            </span>
            <select value={professorId} onChange={(e) => setProfessorId(e.target.value)}>
              <option value="">Todos os Professores</option>
              {professores.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.nome}
                </option>
              ))}
            </select>
          </label>
        </div>

        {turmaId && (
          <button className="btn-editar-horario-turma" onClick={() => abrirEditor(turmaId)}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
            </svg>
            Editar Horário da Turma
          </button>
        )}
      </div>

      {editorAberto && turmaParaEditar && (
        <HorarioEditor turmaId={turmaParaEditar} onClose={fecharEditor} onSave={onEditorSave} />
      )}

      {/* Modal de Ajuda */}
      {showHelpModal && (
        <div className="help-modal-overlay" onClick={() => setShowHelpModal(false)}>
          <div className="help-modal" onClick={(e) => e.stopPropagation()}>
            <div className="help-modal-header">
              <h2>Como Criar Horários - Guia Passo a Passo</h2>
              <button className="help-modal-close" onClick={() => setShowHelpModal(false)}>×</button>
            </div>
            <div className="help-modal-content">
              <div className="help-steps">
                <div className="help-step">
                  <div className="step-number">1</div>
                  <div className="step-content">
                    <h4>Cadastre as Disciplinas</h4>
                    <p>Acesse o menu "Disciplinas" e registre todas as matérias que serão ministradas. Defina o tipo (Nuclear ou Global), carga horária semanal e outras informações relevantes.</p>
                  </div>
                </div>
                <div className="help-step">
                  <div className="step-number">2</div>
                  <div className="step-content">
                    <h4>Cadastre as Salas</h4>
                    <p>No menu "Salas", adicione todas as salas de aula disponíveis. Informe a capacidade e localização para otimizar a distribuição.</p>
                  </div>
                </div>
                <div className="help-step">
                  <div className="step-number">3</div>
                  <div className="step-content">
                    <h4>Cadastre os Professores</h4>
                    <p>Acesse "Professores" e registre todos os docentes. Vincule cada professor às disciplinas que pode ministrar.</p>
                  </div>
                </div>
                <div className="help-step">
                  <div className="step-number">4</div>
                  <div className="step-content">
                    <h4>Cadastre os Cursos</h4>
                    <p>No menu "Cursos", crie os cursos oferecidos pela instituição.</p>
                  </div>
                </div>
                <div className="help-step">
                  <div className="step-number">5</div>
                  <div className="step-content">
                    <h4>Cadastre as Turmas</h4>
                    <p>Acesse "Turmas" e crie as turmas. Vincule cada turma ao curso correspondente, sala padrão e disciplinas obrigatórias.</p>
                  </div>
                </div>
                <div className="help-step">
                  <div className="step-number">6</div>
                  <div className="step-content">
                    <h4>Gere os Horários Automáticos</h4>
                    <p>No menu "Horários", use a opção "Gerar Horários Automáticos" para criar uma distribuição inicial baseada nas regras do sistema.</p>
                  </div>
                </div>
                <div className="help-step">
                  <div className="step-number">7</div>
                  <div className="step-content">
                    <h4>Ajuste Manualmente se Necessário</h4>
                    <p>Use o "Editor de Horários" para fazer ajustes finos, mover aulas ou corrigir conflitos específicos.</p>
                  </div>
                </div>
                <div className="help-step">
                  <div className="step-number">8</div>
                  <div className="step-content">
                    <h4>Verifique e Salve</h4>
                    <p>Revise o horário gerado, verifique conflitos e salve as alterações.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <HorarioList keyProp={updateList} turmaId={turmaId} professorId={professorId} cursoId={cursoId} />
    </div>
  )
}
